import type { TokenClaims } from './types';

/**
 * Decode a phone token's claims WITHOUT verifying the signature.
 *
 * Verification is the server's job (FreeSWITCH's directory validates the token
 * on registration). The SDK decodes it purely to read `sub` (the extension) and
 * `acc` (the account) so it can build the SIP URI, and to surface scope/expiry
 * to the caller. A tampered token simply fails at registration.
 *
 * Returns `null` if the string isn't a well-formed JWT.
 */
export function decodeToken(token: string): TokenClaims | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json) as TokenClaims;
  } catch {
    return null;
  }
}

/** True if the token's `exp` claim is in the past. */
export function isExpired(claims: TokenClaims): boolean {
  return typeof claims.exp === 'number' && claims.exp * 1000 <= Date.now();
}
